/**
 * Custom Validations (CV-5, LIQ-2272): a rule's criteria written as a formula the user types, e.g.
 *
 *   IF Claim_Status IN ("Closed", "Re-closed")
 *   THEN Date_Closed IS NOT EMPTY AND Reserve_Indemnity = 0
 *
 * parseFormula turns the text into criteria format v1 (CRITERIA_FORMAT.md in lincoln, criteria.py in
 * bdx-validation-service), or says plainly what is wrong and where (from / to, character offsets),
 * so the screen can point at it. formatFormula turns saved criteria back into the text, so Edit and
 * New version open a rule as a formula: parseFormula(formatFormula(criteria)) gives the same
 * criteria back. The rest helps the editor: the word being typed, the columns that match it, and the
 * buttons that insert a piece of formula.
 *
 * The language:
 *   columns    as the BDX spells them (any case): Date_Closed
 *   values     "text" (\" for a quote inside), 12.5, -3, "2026-01-31" (a date where the other side
 *              is a date), TRUE / FALSE
 *   checks     IS EMPTY, IS NOT EMPTY, = and != (also ≠, <>), <, <= (≤), >, >= (≥),
 *              IN ("a", "b"), NOT IN (…), CONTAINS "x", CONTAINS WORD "x", STARTS WITH "x",
 *              ENDS WITH "x"; = / != on numbers take WITHIN 0.03
 *   sums       + - * / on numbers, * and / before + and -, brackets to group
 *   joining    AND, OR (AND first), NOT ( … ), brackets, IF … THEN …
 */
import { checkCriteria, SCHEMA_VERSION } from "./criteriaCheck.js";

export const KEYWORDS = ["IF", "THEN", "AND", "OR", "NOT", "IN", "IS", "EMPTY", "WITHIN", "CONTAINS", "WORD", "STARTS", "ENDS", "WITH", "TRUE", "FALSE"];

// The editor's insert buttons: [what the button shows, what it inserts at the caret].
export const INSERTS = [
	["IF", "IF "], ["THEN", " THEN "], ["AND", " AND "], ["OR", " OR "], ["NOT", "NOT ("], ["(", "("], [")", ")"],
	["=", " = "], ["≠", " ≠ "], ["<", " < "], ["≤", " ≤ "], [">", " > "], ["≥", " ≥ "],
	["IN", ' IN ("", "")'], ["IS EMPTY", " IS EMPTY"], ["IS NOT EMPTY", " IS NOT EMPTY"],
	["CONTAINS", ' CONTAINS ""'], ["+", " + "], ["−", " - "], ["WITHIN", " WITHIN 0.03"],
];

const ORDER_OPS = ["<", "<=", ">", ">="];
const OP_TEXT = { "=": "=", "!=": "≠", "<": "<", "<=": "≤", ">": ">", ">=": "≥" };
const TEXT_OP_WORDS = { contains: "CONTAINS", starts_with: "STARTS WITH", ends_with: "ENDS WITH" };
const TYPE_NAMES = { text: "text", numeric: "a number", date: "a date", boolean: "true / false" };
const QUOTES = ['"', "“", "”"];
const isIsoDate = (value) => /^\d{4}-\d{2}-\d{2}$/.test(value);

// --- Reading the text ---------------------------------------------------------------------------

function problem(message, from, to) {
	return { kind: "error", message, from, to };
}

function unfinished(message, at) {
	return { kind: "incomplete", message, from: at, to: at };
}

function tokenize(src) {
	const tokens = [];
	let i = 0;
	while (i < src.length) {
		const c = src[i];
		if (/\s/.test(c)) {
			i += 1;
			continue;
		}
		const from = i;
		if (QUOTES.includes(c)) {
			let j = i + 1;
			let value = "";
			while (j < src.length && !QUOTES.includes(src[j])) {
				if (src[j] === "\\" && j + 1 < src.length) {
					value += src[j + 1];
					j += 2;
				} else {
					value += src[j];
					j += 1;
				}
			}
			if (j >= src.length) {
				return { stop: unfinished('Not finished: close the text with a quote (").', src.length) };
			}
			tokens.push({ t: "str", v: value, from, to: j + 1 });
			i = j + 1;
			continue;
		}
		const two = src.slice(i, i + 2);
		if (["<=", ">=", "!=", "<>"].includes(two)) {
			tokens.push({ t: "op", v: two === "<>" ? "!=" : two, from, to: i + 2 });
			i += 2;
			continue;
		}
		if ("=<>≤≥≠".includes(c)) {
			tokens.push({ t: "op", v: { "≤": "<=", "≥": ">=", "≠": "!=" }[c] || c, from, to: i + 1 });
			i += 1;
			continue;
		}
		if ("+-−*/×÷".includes(c)) {
			tokens.push({ t: "math", v: { "−": "-", "×": "*", "÷": "/" }[c] || c, from, to: i + 1 });
			i += 1;
			continue;
		}
		if (c === "(" || c === ")" || c === ",") {
			tokens.push({ t: c, from, to: i + 1 });
			i += 1;
			continue;
		}
		const number = /^\d+(\.\d+)?/.exec(src.slice(i));
		if (number) {
			tokens.push({ t: "num", v: Number(number[0]), from, to: i + number[0].length });
			i += number[0].length;
			continue;
		}
		const word = /^[A-Za-z_][A-Za-z0-9_]*/.exec(src.slice(i));
		if (word) {
			const upper = word[0].toUpperCase();
			tokens.push(KEYWORDS.includes(upper) ? { t: "kw", v: upper, from, to: i + word[0].length } : { t: "id", v: word[0], from, to: i + word[0].length });
			i += word[0].length;
			continue;
		}
		return { stop: problem(`“${c}” can't be used in a rule.`, i, i + 1) };
	}
	return { tokens };
}

/** The column a misspelt name most likely meant, or null. */
export function closestColumn(word, names) {
	const wanted = String(word).toLowerCase();
	let best = null;
	let bestScore = 0;
	names.forEach((name) => {
		const lower = name.toLowerCase();
		let score = 0;
		while (score < wanted.length && score < lower.length && wanted[score] === lower[score]) score += 1;
		if (lower.includes(wanted)) score += wanted.length;
		if (score > bestScore) {
			bestScore = score;
			best = name;
		}
	});
	return bestScore >= 4 ? best : null;
}

class Stop {
	constructor(result) {
		this.result = result;
	}
}

function parseTokens(tokens, columnTypes, srcLength) {
	let p = 0;
	const names = Object.keys(columnTypes);
	const byLower = {};
	names.forEach((name) => {
		byLower[name.toLowerCase()] = name;
	});
	const peek = () => tokens[p];
	const kw = (value) => !!tokens[p] && tokens[p].t === "kw" && tokens[p].v === value;
	const show = (tok) => (tok.t === "str" ? `"${tok.v}"` : tok.v !== undefined ? String(tok.v) : tok.t);
	const fail = (message, tok) => {
		throw new Stop(problem(message, tok ? tok.from : srcLength, tok ? tok.to : srcLength));
	};
	const expect = (what) => {
		if (p >= tokens.length) throw new Stop(unfinished(`Not finished: ${what}.`, srcLength));
	};
	const typeName = (type) => TYPE_NAMES[type] || "a value";

	// A value: { type, node, text, from, to, grouped }. type "str" is a quoted value whose type (text or
	// date) is settled by what it's compared with.
	function factor(what) {
		expect(what);
		const tok = peek();
		if (tok.t === "(") {
			p += 1;
			const inner = sum("a number or a number column inside the brackets");
			expect("close the bracket with )");
			if (peek().t !== ")") fail(`Expected ) to close the bracket, not “${show(peek())}”.`, peek());
			p += 1;
			return { ...inner, grouped: true, from: tok.from, to: tokens[p - 1].to };
		}
		if (tok.t === "math" && tok.v === "-" && tokens[p + 1] && tokens[p + 1].t === "num") {
			p += 2;
			return { type: "numeric", node: { number: -tokens[p - 1].v }, text: `-${tokens[p - 1].v}`, from: tok.from, to: tokens[p - 1].to };
		}
		p += 1;
		if (tok.t === "id") {
			const name = byLower[tok.v.toLowerCase()];
			if (!name) {
				const guess = closestColumn(tok.v, names);
				fail(`“${tok.v}” isn't a column${guess ? ` — did you mean ${guess}?` : "."}`, tok);
			}
			return { type: columnTypes[name], node: { column: name }, text: name.replace(/_/g, " "), from: tok.from, to: tok.to };
		}
		if (tok.t === "num") return { type: "numeric", node: { number: tok.v }, text: String(tok.v), from: tok.from, to: tok.to };
		if (tok.t === "str") return { type: "str", raw: tok.v, text: `"${tok.v}"`, from: tok.from, to: tok.to };
		if (tok.t === "kw" && (tok.v === "TRUE" || tok.v === "FALSE")) {
			return { type: "boolean", node: { boolean: tok.v === "TRUE" }, text: tok.v, from: tok.from, to: tok.to };
		}
		p -= 1;
		return fail(`Expected ${what}, not “${show(tok)}”.`, tok);
	}

	function combine(first, op, next) {
		[first, next].forEach((side) => {
			if (side.type !== "numeric") fail(`${op} needs numbers, and ${side.text} is ${typeName(side.type)}.`, side);
		});
		const merge = first.node.op === op && !first.grouped;
		return {
			type: "numeric",
			node: { op, operands: merge ? first.node.operands.concat([next.node]) : [first.node, next.node] },
			text: `${first.text} ${op} ${next.text}`,
			from: first.from,
			to: next.to,
		};
	}

	function term(what) {
		let value = factor(what);
		while (peek() && peek().t === "math" && (peek().v === "*" || peek().v === "/")) {
			const op = peek().v;
			p += 1;
			value = combine(value, op, factor(`a number or a number column after ${op}`));
		}
		return value;
	}

	function sum(what) {
		let value = term(what);
		while (peek() && peek().t === "math" && (peek().v === "+" || peek().v === "-")) {
			const op = peek().v;
			p += 1;
			value = combine(value, op, term(`a number or a number column after ${op}`));
		}
		return value;
	}

	// A quoted value becomes a date next to a date, else text.
	function settle(side, other) {
		if (side.type !== "str") return side;
		const otherType = other && other.type;
		const asDate = otherType === "date" || (!otherType && isIsoDate(side.raw)) || (otherType === "str" && isIsoDate(side.raw));
		if (asDate) {
			if (!isIsoDate(side.raw)) {
				const why = otherType === "date" ? `${other.text} is a date, so write the value` : "Write the date";
				fail(`${why} as "YYYY-MM-DD", e.g. "2026-01-31".`, side);
			}
			return { ...side, type: "date", node: { date: side.raw } };
		}
		if (!side.raw.trim()) fail("Text in quotes can't be empty — to check for an empty value use IS EMPTY.", side);
		return { ...side, type: "text", node: { text: side.raw } };
	}

	function listValues() {
		expect('open the list with (, e.g. IN ("Yes", "No")');
		if (peek().t !== "(") fail('IN needs a list in brackets, e.g. IN ("Yes", "No").', peek());
		const open = peek();
		p += 1;
		const items = [];
		for (;;) {
			expect("finish the list and close it with )");
			const tok = peek();
			if (tok.t === "str") {
				if (!tok.v.trim()) fail("Fill in the empty value in the list.", tok);
				items.push(tok.v);
			} else if (tok.t === "num") {
				items.push(tok.v);
			} else if (tok.t === "math" && tok.v === "-" && tokens[p + 1] && tokens[p + 1].t === "num") {
				p += 1;
				items.push(-tokens[p].v);
			} else {
				fail(`List values go in quotes, e.g. "Yes" — not “${show(tok)}”.`, tok);
			}
			p += 1;
			expect("close the list with )");
			if (peek().t === ",") {
				p += 1;
				continue;
			}
			if (peek().t === ")") {
				p += 1;
				break;
			}
			fail(`Separate list values with commas and close the list with ), not “${show(peek())}”.`, peek());
		}
		return { items, from: open.from, to: tokens[p - 1].to };
	}

	function comparison() {
		const left = sum("a column");
		const subject = left.text;
		expect(`say what ${subject} must be, e.g. IS NOT EMPTY, = or IN`);
		if (kw("IS")) {
			p += 1;
			let negate = false;
			if (kw("NOT")) {
				p += 1;
				negate = true;
			}
			expect("write EMPTY");
			if (!kw("EMPTY")) fail("After IS write EMPTY or NOT EMPTY.", peek());
			p += 1;
			if (!left.node || (!left.node.column && !left.node.operands)) fail("IS EMPTY needs a column, not a fixed value.", left);
			return { type: negate ? "is_not_null" : "is_null", operand: left.node };
		}
		let listOp = null;
		if (kw("NOT")) {
			const tok = peek();
			p += 1;
			expect("write IN ( … )");
			if (!kw("IN")) fail("After NOT write IN ( … ).", peek() || tok);
			listOp = "not_in";
		} else if (kw("IN")) {
			listOp = "in";
		}
		if (listOp) {
			p += 1;
			const list = listValues();
			const numeric = list.items.every((item) => typeof item === "number");
			const text = list.items.every((item) => typeof item === "string");
			if (!numeric && !text) fail("A list must be all text or all numbers.", list);
			if (left.type === "str" || left.type === "boolean") fail("IN needs a column on its left.", left);
			if (left.type === "date") fail(`IN doesn't work on dates like ${subject}.`, left);
			if (numeric && left.type && left.type !== "numeric") fail(`${subject} is text, so the list values need quotes.`, list);
			if (text && left.type === "numeric") fail(`${subject} is a number, so the list values shouldn't be in quotes.`, list);
			return { type: "compare", op: listOp, left: left.node, right: { list: list.items } };
		}
		if (kw("CONTAINS") || kw("STARTS") || kw("ENDS")) {
			const word = peek().v;
			p += 1;
			let wholeWord = false;
			if (word === "CONTAINS" && kw("WORD")) {
				p += 1;
				wholeWord = true;
			}
			if (word !== "CONTAINS") {
				expect("write WITH");
				if (!kw("WITH")) fail(`After ${word} write WITH.`, peek());
				p += 1;
			}
			const name = word === "CONTAINS" ? "CONTAINS" : `${word} WITH`;
			const right = settle(sum(`text in quotes, e.g. ${name} "B"`), { type: "text" });
			const settledLeft = settle(left, { type: "text" });
			if (settledLeft.type && settledLeft.type !== "text") fail(`${name} works on text, and ${subject} is ${typeName(settledLeft.type)}.`, left);
			if (right.type !== "text") fail(`${name} needs text, e.g. ${name} "B".`, right);
			const op = { CONTAINS: "contains", STARTS: "starts_with", ENDS: "ends_with" }[word];
			const node = { type: "compare", op, left: settledLeft.node, right: right.node };
			if (wholeWord) node.whole_word = true;
			return node;
		}
		const tok = peek();
		if (tok.t !== "op") fail(`Expected a check after ${subject}, e.g. IS NOT EMPTY, = or IN — not “${show(tok)}”.`, tok);
		p += 1;
		let right = sum(`what ${subject} is compared with`);
		const settledLeft = settle(left, right);
		right = settle(right, settledLeft);
		if (settledLeft.type && right.type && settledLeft.type !== right.type) {
			fail(`${subject} is ${typeName(settledLeft.type)} but ${right.text} is ${typeName(right.type)}, so they can't be compared.`, { from: left.from, to: right.to });
		}
		if (ORDER_OPS.includes(tok.v) && (settledLeft.type === "text" || settledLeft.type === "boolean")) {
			fail(`${OP_TEXT[tok.v]} works on numbers and dates, and ${subject} is ${typeName(settledLeft.type)} — use = or IN.`, tok);
		}
		const node = { type: "compare", op: tok.v, left: settledLeft.node, right: right.node };
		if (kw("WITHIN")) {
			const within = peek();
			p += 1;
			expect("give the tolerance, e.g. WITHIN 0.03");
			if (peek().t !== "num") fail("WITHIN needs a number, e.g. WITHIN 0.03.", peek());
			if ((tok.v !== "=" && tok.v !== "!=") || settledLeft.type !== "numeric") fail("WITHIN only works with = or ≠ on numbers.", within);
			node.tolerance = peek().v;
			p += 1;
		}
		return node;
	}

	// Brackets at the start of a condition hold either a condition or a sum: "(Paid + Reserve) = …".
	function bracketsHoldASum() {
		let depth = 0;
		for (let i = p; i < tokens.length; i += 1) {
			if (tokens[i].t === "(") depth += 1;
			if (tokens[i].t === ")") {
				depth -= 1;
				if (depth === 0) {
					const after = tokens[i + 1];
					return !!after && (after.t === "op" || after.t === "math" || (after.t === "kw" && ["IS", "IN", "NOT", "CONTAINS", "STARTS", "ENDS"].includes(after.v)));
				}
			}
		}
		return false;
	}

	function primary(what) {
		expect(what);
		if (kw("IF")) {
			p += 1;
			const condition = orExpr("a condition after IF");
			expect("add THEN and what must be true");
			if (!kw("THEN")) fail(`Expected THEN after the IF part, not “${show(peek())}”.`, peek());
			p += 1;
			return { type: "if", if: condition, then: orExpr("what must be true after THEN") };
		}
		if (peek().t === "(" && !bracketsHoldASum()) {
			p += 1;
			const inner = orExpr("a condition inside the brackets");
			expect("close the bracket with )");
			if (peek().t !== ")") fail(`Expected ) to close the bracket, not “${show(peek())}”.`, peek());
			p += 1;
			return inner;
		}
		return comparison();
	}

	function notExpr(what) {
		if (kw("NOT")) {
			p += 1;
			return { type: "not", condition: notExpr("a condition after NOT") };
		}
		return primary(what);
	}

	function andExpr(what) {
		const items = [notExpr(what)];
		while (kw("AND")) {
			p += 1;
			items.push(notExpr("a condition after AND"));
		}
		return items.length > 1 ? { type: "and", conditions: items } : items[0];
	}

	function orExpr(what) {
		const items = [andExpr(what)];
		while (kw("OR")) {
			p += 1;
			items.push(andExpr("a condition after OR"));
		}
		return items.length > 1 ? { type: "or", conditions: items } : items[0];
	}

	const condition = orExpr("a condition");
	if (p < tokens.length) fail(`Unexpected “${show(tokens[p])}” — join conditions with AND or OR.`, tokens[p]);
	return condition;
}

/**
 * The formula as criteria.
 * @param text what the user typed
 * @param columnTypes column name -> text / numeric / date / boolean (GET /api/custom-validations/columns)
 * @returns { ok: true, criteria } or { ok: false, kind, message, from, to } - kind "empty" (nothing
 *   typed), "incomplete" (not finished yet, e.g. a column half typed), or "error" (something to fix,
 *   with from / to pointing at it in the text)
 */
export function parseFormula(text, columnTypes = {}) {
	const src = String(text || "");
	if (!src.trim()) {
		return { ok: false, kind: "empty", message: "Start typing, click a button above, or try an example.", from: 0, to: 0 };
	}
	const lexed = tokenize(src);
	if (lexed.stop) return { ok: false, ...lexed.stop };
	const last = lexed.tokens[lexed.tokens.length - 1];
	const lower = Object.keys(columnTypes).map((name) => name.toLowerCase());
	if (last && last.t === "id" && last.to === src.length && !lower.includes(last.v.toLowerCase())
		&& lower.some((name) => name.includes(last.v.toLowerCase()))) {
		return { ok: false, kind: "incomplete", message: "Keep typing, or pick a column from the list.", from: last.from, to: last.to };
	}
	let condition;
	try {
		condition = parseTokens(lexed.tokens, columnTypes, src.length);
	} catch (stop) {
		if (stop instanceof Stop) return { ok: false, ...stop.result };
		throw stop;
	}
	const criteria = { schema_version: SCHEMA_VERSION, condition };
	// What the backend would still refuse (e.g. nesting deeper than it allows, dividing by 0).
	const refused = checkCriteria(criteria, Object.keys(columnTypes).length ? columnTypes : null);
	if (refused.length) {
		return { ok: false, kind: "error", message: `This can't be saved: ${refused[0].message}.`, from: 0, to: src.length };
	}
	return { ok: true, criteria };
}

// --- Writing the text -----------------------------------------------------------------------------

const PRECEDENCE = { "+": 1, "-": 1, "*": 2, "/": 2 };

function quote(value) {
	return `"${String(value).replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;
}

function operandText(o) {
	if ("column" in o) return o.column;
	if ("text" in o) return quote(o.text);
	if ("number" in o) return String(o.number);
	if ("date" in o) return quote(o.date);
	if ("boolean" in o) return o.boolean ? "TRUE" : "FALSE";
	if ("list" in o) return `(${o.list.map((item) => (typeof item === "number" ? String(item) : quote(item))).join(", ")})`;
	// A sum: brackets wherever reading left to right with * and / first would group it differently.
	return o.operands
		.map((inner, index) => {
			const text = operandText(inner);
			if (!inner.operands) return text;
			const lower = PRECEDENCE[inner.op] < PRECEDENCE[o.op];
			const sameLevel = PRECEDENCE[inner.op] === PRECEDENCE[o.op];
			const bracket = lower || (index === 0 ? inner.op === o.op : sameLevel);
			return bracket ? `(${text})` : text;
		})
		.join(` ${o.op} `);
}

function conditionText(c, top) {
	switch (c.type) {
		case "and":
		case "or":
			return c.conditions
				.map((inner) => (["and", "or", "if"].includes(inner.type) ? `(${conditionText(inner, false)})` : conditionText(inner, false)))
				.join(` ${c.type.toUpperCase()} `);
		case "not":
			return `NOT (${conditionText(c.condition, false)})`;
		case "if": {
			const part = (inner) => (inner.type === "if" ? `(${conditionText(inner, false)})` : conditionText(inner, false));
			return `IF ${part(c.if)}${top ? "\n" : " "}THEN ${part(c.then)}`;
		}
		case "is_null":
			return `${operandText(c.operand)} IS EMPTY`;
		case "is_not_null":
			return `${operandText(c.operand)} IS NOT EMPTY`;
		default: {
			let check;
			if (c.op === "in" || c.op === "not_in") check = c.op === "in" ? "IN" : "NOT IN";
			else if (TEXT_OP_WORDS[c.op]) check = c.op === "contains" && c.whole_word ? "CONTAINS WORD" : TEXT_OP_WORDS[c.op];
			else check = OP_TEXT[c.op];
			const within = c.tolerance !== undefined ? ` WITHIN ${c.tolerance}` : "";
			return `${operandText(c.left)} ${check} ${operandText(c.right)}${within}`;
		}
	}
}

/** A rule's criteria as formula text (an IF … THEN at the top puts THEN on its own line). */
export function formatFormula(criteria) {
	return criteria && criteria.condition ? conditionText(criteria.condition, true) : "";
}

// --- Helping the editor -----------------------------------------------------------------------------

/** Whether the caret is inside quoted text, read the way the tokenizer reads it. */
function insideQuotes(before) {
	let inside = false;
	for (let i = 0; i < before.length; i += 1) {
		if (inside && before[i] === "\\") i += 1;
		else if (QUOTES.includes(before[i])) inside = !inside;
	}
	return inside;
}

/** The word ending at the caret, if a column name could be being typed: { word, from }, or null. */
export function wordBefore(text, caret) {
	const before = String(text).slice(0, caret);
	const match = /[A-Za-z_][A-Za-z0-9_]*$/.exec(before);
	if (!match || insideQuotes(before)) return null;
	return { word: match[0], from: caret - match[0].length };
}

/**
 * The columns matching a half-typed word, names that start with it first.
 * @param columns [{ name, data_type, description }] (GET /api/custom-validations/columns)
 */
export function suggestColumns(word, columns, limit = 6) {
	const wanted = String(word || "").toLowerCase();
	if (wanted.length < 2 || KEYWORDS.includes(wanted.toUpperCase())) return [];
	if (columns.some((column) => column.name.toLowerCase() === wanted)) return [];
	return columns
		.filter((column) => column.name.toLowerCase().includes(wanted))
		.sort((a, b) => Number(!a.name.toLowerCase().startsWith(wanted)) - Number(!b.name.toLowerCase().startsWith(wanted))
			|| a.name.length - b.name.length)
		.slice(0, limit);
}

/** The text with the half-typed word at the caret replaced by a column name: { text, caret }. */
export function insertColumn(text, caret, name) {
	const found = wordBefore(text, caret);
	const start = found ? found.from : caret;
	const before = `${text.slice(0, start)}${name} `;
	const after = text.slice(caret).replace(/^[A-Za-z0-9_]*\s*/, "");
	return { text: before + after, caret: before.length };
}

/** The text with a piece of formula inserted at the caret: { text, caret }. */
export function insertAt(text, caret, snippet) {
	const before = text.slice(0, caret) + snippet;
	return { text: before + text.slice(caret), caret: before.length };
}
